import * as _ from 'underscore';

import { SiteUrls } from '@tableau/shared-urls';

import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import { watchUrlForBreadcrumbUpdates } from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { BreadcrumbStateInfo } from '../../app/content/breadcrumbs/BreadcrumbStateInfo';
import { ScheduleGrid } from '../../app/content/ScheduleGrid';
import { IMainScope } from '../../app/IMainScope';
import { IPlaceTitleScope } from '../../app/IPlaceTitleScope';
import { ServerUrls } from '../../app/ServerUrls';
import { Messages } from '../../codegen/Localize';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ListResult } from '../fetchers/ListResult';
import { IScheduleActionNameFilter } from '../filters/ScheduleActionNameFilter';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { IScheduleTypeNameFilter } from '../filters/ScheduleTypeNameFilter';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ActionContext, IActionContext } from '../helpers/ActionContext';
import { SchedulingErrorCode } from '../helpers/ErrorCodes';
import { Filter } from '../helpers/Filter';
import { ScheduleContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Schedules } from '../resources/Schedules';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { IResult } from '../ServerApi/types/IResponse';
import { ISchedule, ScheduleActions } from '../ServerApi/types/ISchedule';
import { ActionHelpers } from '../services/ActionHelpers';
import { IActionButton, IListActionButton } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { CreateScheduleAction } from '../services/CreateScheduleAction';
import { DeleteSchedulesAction } from '../services/DeleteSchedulesAction';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { RenameAction } from '../services/RenameAction';
import { RunSchedulesAction } from '../services/RunSchedulesAction';
import { RunServerSchedulesAction } from '../services/RunServerSchedulesAction';
import { SchedulePermissions } from '../services/SchedulePermissions';
import { SchedulesConfig } from '../services/SchedulesConfig';
import { ServerService } from '../services/ServerService';
import { SetScheduleStatusAction } from '../services/SetScheduleStatusAction';
import { SorterFactory } from '../services/SorterFactory';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';

export interface ISchedulesScope
extends IMainScope,
        IActionsMenuScope<ISchedule, {}>,
        IPlaceTitleScope,
        ListActionsScope,
        SubplaceWithActionButtonsScope<ISchedule, {}>,
        SubplaceWithDataGridScope<ISchedule> {
  schedules: { count: number };
  scheduleUrl: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
}

export function getSchedulesPlaceBreadcrumb($state: ng.ui.IStateService): BreadcrumbStateInfo {
  const href = $state.includes('**.server.**') ? $state.href('main.server.schedules')
                                               : $state.href('main.site.schedules');
  return {
    id: href,
    text: Messages.allSchedules(),
    href: href,
    testId: 'schedules-link'
  };
}

export class SchedulesCtrl {

  public static $inject = [
    '$location',
    '$scope',
    '$state',
    '$translate',
    'AngularToReactDataGridBridge',
    '$filter',
    'BrowserTitleService',
    'siteId',
    'QueryService',
    'SchedulesConfig',
    'SchedulePermissions',
    'Schedules',
    'SorterFactory',
    'ServerService',
    'CreateScheduleAction',
    'SetScheduleStatusAction',
    'RunServerSchedulesAction',
    'RunSchedulesAction',
    'DeleteSchedulesAction',
    'RenameAction'
  ];

  private query: IQueryUpdater;

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'schedulesEmptyMessage' };

  constructor(
    private $location: ng.ILocationService,
    private $scope: ISchedulesScope,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    private $filter: ng.IFilterService,
            BrowserTitleService: BrowserTitleService,
            siteId: string,
            queryService: QueryService,
    private SchedulesConfig: SchedulesConfig,
    private SchedulePermissions: SchedulePermissions,
            Schedules: Schedules,
            SorterFactory: SorterFactory,
    private ServerService: ServerService,
    private CreateScheduleAction: CreateScheduleAction,
    private SetScheduleStatusAction: SetScheduleStatusAction,
    private RunServerSchedulesAction: RunServerSchedulesAction,
    private RunSchedulesAction: RunSchedulesAction,
    private DeleteSchedulesAction: DeleteSchedulesAction,
    private RenameAction: RenameAction
  ) {

    const selectedItems = new Set<ISchedule>((schedule: ISchedule) => schedule.id);
    const actions = this.createActions(selectedItems, siteId);
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, selectedItems);
    $scope.selectedItems = selectedItems;
    $scope.displayScheduleDescriptionAsName = ServerService.displayScheduleDescriptionAsName();

    this.configureForReactDataGrid(actions, getActionTarget);

    BrowserTitleService.setTitleWithPlace({translatablePlaceName: 'schedules'});

    $scope.sorter = SorterFactory.create(ScheduleContentType.defaultOrder);
    $scope.$on('$destroy', () => $scope.sorter.detach());

    this.query = queryService.createQueryUpdater(
      $scope,
      ScheduleContentType,
      Schedules,
      (resourceQuery) => {
        if (siteId) {
          resourceQuery = resourceQuery.addFilter(Filter.fieldEq(FilterFieldNames.siteId, siteId));
        }
        if (!this.ServerService.flowsOnServerEnabled()) {
          resourceQuery = resourceQuery.addFilter(Filter.stringFieldInArray('scheduledAction', [ScheduleActions.Extract, ScheduleActions.Subscription]));
        }
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: $scope, sliceable: resourceQuery });

        $scope.sliceable = resourceQuery;
        $scope.sorter.setOrder(resourceQuery.getOrder());
        resourceQuery.getCount().then((result: number) => {
          this.$scope.schedules.count = result;
        });
      },
      {}
    );
    this.query.refresh();

    $scope.placeTitleClicked = () => {
      this.refreshSchedules();
    };

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    $scope.listActions = [{
      name: 'create-new-schedule',
      translate: 'newSchedule',
      isAvailable: () => ServerService.isSessionServerAdmin(),
      execute: ($event: JQueryEventObject) => {
        CreateScheduleAction.createSchedule(ActionContext.fromEvent($event)).then(() => this.refreshSchedules());
      }
    }];
    $scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    $scope.sliceChanged = (newSlice: ListResult<ISchedule>) => {
      $scope.selectedItems.updateItems(newSlice.items);
      $scope.schedules.count = newSlice.total;
    };

    $scope.schedules = { count: 0 };

    $scope.scheduleUrl = (schedule: ISchedule) => {
      return siteId
        ? this.SchedulesConfig.siteUrlFor(schedule)
        : this.SchedulesConfig.serverUrlFor(schedule);
    };

    $scope.emptyTemplateModel = SchedulesCtrl.emptyTemplateModel;

    this.initBreadcrumbs();
  }

  private configureForReactDataGrid(actions: IActionButton<ISchedule, {}>[],
                                    getActionTarget: () => IActionTarget<ISchedule, {}>) {

    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search
    });

    const columns = ScheduleGrid.columns(
      (id: string) => this.$translate.instant(id),
      this.$scope,
      actions,
      getActionTarget,
      this.isServerPage(),
      ServerUrls.getInstance(),
      siteUrls,
      <IScheduleTypeNameFilter>this.$filter('scheduleTypeName'),
      <IScheduleActionNameFilter>this.$filter('scheduleActionName'),
      <IScheduleFrequencyDescriptionFilter>this.$filter('scheduleFrequencyDescription'),
      this.$scope.displayScheduleDescriptionAsName
    );

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(SchedulesCtrl.emptyTemplateModel),
      canShowFilterPanel: ScheduleContentType.canShowFilterPanel
    });
  }

  private isServerPage() {
    return this.$state.includes('main.server.**');
  }

  private createActions(selectedItems: Set<ISchedule>, siteId: string): IActionButton<ISchedule, {}>[] {
    return [
      {
        name: 'schedules-run-now',
        translate: 'runNow_action',
        isAvailable: (schedules: Set<ISchedule>) =>
          schedules.size() > 0 && this.SchedulePermissions.canRunSchedule(),
        execute: (items: Set<ISchedule>, context: IActionContext) => {
          if(!siteId) {
            this.RunServerSchedulesAction.execute(items, context)
              .then((result: IResult) => {
                ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingScheduleNotFound);
                this.refreshSchedules();
              });
          } else {
            this.RunSchedulesAction.execute(items, context)
              .then((result: IResult) => {
                ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingScheduleNotFound);
                this.refreshSchedules();
              });
          }
        }
      },
      {
        name: 'enableSchedules',
        translate: 'enable_action',
        isAvailable: (schedules: Set<ISchedule>) =>
          schedules.size() > 0 && this.SchedulePermissions.canEditSchedule(),
        isDisabled: (schedules: Set<ISchedule>) => {
          return !_.any(schedules.toArray(), (schedule: ISchedule) => !this.isScheduleEnabled(schedule));
        },
        execute: (items: Set<ISchedule>, context: IActionContext) => {
          this.SetScheduleStatusAction.confirmEnableSchedules(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingScheduleNotFound);
              this.refreshSchedules();
            });
        }
      },
      {
        name: 'disableSchedules',
        translate: 'disable_action',
        isAvailable: (schedules: Set<ISchedule>) =>
          schedules.size() > 0 && this.SchedulePermissions.canEditSchedule(),
        isDisabled: (schedules: Set<ISchedule>) => {
          return !_.any(schedules.toArray(), (schedule: ISchedule) => this.isScheduleEnabled(schedule));
        },
        execute: (items: Set<ISchedule>, context: IActionContext) => {
          this.SetScheduleStatusAction.confirmDisableSchedules(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingScheduleNotFound);
              this.refreshSchedules();
            });
        }
      },
      {
        name: 'rename-schedule',
        translate: 'rename_action',
        isAvailable: (schedules: Set<ISchedule>) =>
          schedules.size() > 0 && this.SchedulePermissions.canEditSchedule(),
        isDisabled: (schedules: Set<ISchedule>) => schedules.size() !== 1,
        execute: (items: Set<ISchedule>, context: IActionContext) => {
          this.RenameAction.renameSchedule(items.first(), context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingScheduleNotFound);
              this.refreshSchedules();
            });
        }
      },
      {
        name: 'edit-schedule',
        translate: 'editScheduleSettings_action',
        isAvailable: (schedules: Set<ISchedule>) =>
          schedules.size() > 0 && this.SchedulePermissions.canEditSchedule(),
        isDisabled: (schedules: Set<ISchedule>) => schedules.size() !== 1,
        execute: (items: Set<ISchedule>, context: IActionContext) => {
          this.CreateScheduleAction.editSchedule(items.first(), context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingScheduleNotFound);
              this.refreshSchedules();
            });
        }
      },
      {
        name: 'delete-schedule',
        translate: 'delete_action',
        isAvailable: (schedules: Set<ISchedule>) =>
          schedules.size() > 0 && this.SchedulePermissions.canEditSchedule(),
        execute: (items: Set<ISchedule>, context: IActionContext) => {
          this.DeleteSchedulesAction.deleteSchedules(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectAllExceptErrors(selectedItems, result, SchedulingErrorCode.SchedulingScheduleNotFound);
              this.refreshSchedules();
            });
        }
      }
    ];
  }

  private refreshSchedules() {
    this.query.refresh();
  }

  private isScheduleEnabled(schedule: ISchedule): boolean {
    return !!schedule.runNextAt;
  }

  private initBreadcrumbs() {
    const breadcrumbs: BreadcrumbStateInfo[] = [ getSchedulesPlaceBreadcrumb(this.$state) ];
    appStore.dispatch(updateBreadcrumbs(breadcrumbs));

    const currentPath = this.$location.path();
    watchUrlForBreadcrumbUpdates(this.$scope, this.$location, () => this.$location.path() === currentPath);
  }

}

Module.instance().controller('SchedulesCtrl', SchedulesCtrl);
